export { OneAuthClient } from "./client";
export { OneAuthError } from "./errors";
export { SIGNING_ERROR_CODES, isSigningErrorCode } from "./types";
export {
  definePermissions,
  type DefinedPermissions,
  type DefinePermissionsConfig,
} from "./permissions";
export { createCrossChainPermission } from "./crossChainPermissions";
export type {
  CreateCrossChainPermissionInput,
  CrossChainPermit,
  CrossChainSettlementLayer,
} from "./crossChainPermissions";
export type { Permission } from "@rhinestone/sdk";
export { createOneAuthProvider } from "./provider";
export type {
  OneAuthProvider,
  OneAuthProviderOptions,
} from "./provider";
export { createOneAuthConnection } from "./connection";
export type {
  OneAuthConnection,
  OneAuthConnectionConfig,
  OneAuthConnectionConfigWithClient,
  OneAuthConnectionOptions,
  OneAuthConnectOptions,
  OneAuthConnectError,
  OneAuthSession,
} from "./connection";
export { createPasskeyAccount } from "./account";
export type { PasskeyAccount } from "./account";
export type {
  PasskeyProviderConfig,
  SigningRequestOptions,
  SigningResult,
  SigningSuccess,
  SigningError,
  SigningErrorCode,
  PasskeyCredentials,
  WebAuthnSignature,
  CreateSigningRequestResponse,
  SigningRequestStatus,
  EmbedOptions,
  AuthResult,
  AuthFlow,
  AuthWithModalOptions,
  LoginWithModalOptions,
  CreateAccountWithModalOptions,
  SignerType,
  // Connect modal types
  ConnectResult,
  // Authentication with challenge signing types
  AuthenticateOptions,
  AuthenticateResult,
  // Signing types (shared base + specific results)
  SigningResultBase,
  SignMessageOptions,
  SignMessageResult,
  // EIP-712 typed data signing types
  SignTypedDataOptions,
  SignTypedDataResult,
  EIP712Domain,
  EIP712Types,
  EIP712TypeField,
  // Transaction review types
  TransactionAction,
  TransactionFees,
  BalanceRequirement,
  TransactionDetails,
  OneAuthErrorDetails,
  OneAuthCandidateError,
  OneAuthResultError,
  // Intent types for Rhinestone orchestrator
  IntentCall,
  IntentTokenRequest,
  SendIntentOptions,
  IntentQuote,
  IntentStatus,
  OrchestratorStatus,
  CloseOnStatus,
  SendIntentResult,
  PrepareIntentResponse,
  ExecuteIntentResponse,
  // Intent history types
  IntentHistoryOptions,
  IntentHistoryItem,
  IntentHistoryResult,
  // Asset portfolio types
  GetAssetsOptions,
  AssetBalance,
  AssetBalanceBucket,
  AssetsResponse,
  // Theme configuration
  ThemeConfig,
  // Consent types
  ConsentField,
  ConsentData,
  CheckConsentOptions,
  CheckConsentResult,
  RequestConsentOptions,
  RequestConsentResult,
  GrantPermissionsOptions,
  GrantPermissionsResult,
  ListSessionGrantsOptions,
  ListSessionGrantsResult,
  SessionGrantRecord,
  SessionGrantChain,
  GrantPermissionContractMetadata,
  SmartSessionPolicy,
  // Batch intent types
  BatchIntentItem,
  SendBatchIntentOptions,
  SendBatchIntentResult,
  BatchIntentItemResult,
  PreparedBatchIntent,
  PrepareBatchIntentResponse,
  // Sponsorship types
  SponsorshipConfig,
  SponsorshipCallbackConfig,
  SponsorshipUrlConfig,
  // Telemetry types
  OneAuthTelemetryAttributeValue,
  OneAuthTelemetryAttributes,
  OneAuthTelemetryConfig,
  OneAuthTelemetryEvent,
  OneAuthTelemetryEventName,
  OneAuthTelemetryFlow,
  OneAuthTelemetryTraceContext,
} from "./types";

// Viem WalletClient integration
export {
  createPasskeyWalletClient,
  encodeWebAuthnSignature,
  hashCalls,
} from "./walletClient";
export type {
  PasskeyWalletClient,
  PasskeyWalletClientConfig,
  TransactionCall,
  SendCallsParams,
} from "./walletClient";

// Batch queue components
export {
  BatchQueueProvider,
  useBatchQueue,
  BatchQueueWidget,
  getChainName,
} from "./batch";
export type {
  BatchedCall,
  BatchQueueIdentity,
  BatchQueueContextValue,
  BatchQueueProviderProps,
  BatchQueueWidgetProps,
} from "./batch";

// Registry helpers for supported chains/tokens (Rhinestone)
export {
  getSupportedChainIds,
  getSupportedChains,
  getAllSupportedChainsAndTokens,
  getSupportedTokens,
  getSupportedTokenSymbols,
  getChainById,
  getChainExplorerUrl,
  getChainRpcUrl,
  resolveTokenAddress,
  isTestnet,
  getTokenAddress,
  getTokenSymbol,
  getTokenDecimals,
  isTokenAddressSupported,
} from "./registry";
export type { TokenConfig, ChainFilterOptions } from "./registry";

// Message signing verification utilities
export {
  hashMessage,
  verifyMessageHash,
  ETHEREUM_MESSAGE_PREFIX,
} from "./verify";
